import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, Phone } from 'lucide-react';
import { Button } from '../ui/Button';
export function Navbar() {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();
  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);
  useEffect(() => {
    setIsOpen(false);
  }, [location]);
  const navLinks = [{
    name: 'The Firm',
    href: '/about'
  }, {
    name: 'Practices',
    href: '/services'
  }, {
    name: 'Our Process',
    href: '/how-it-works'
  }, {
    name: 'For Employers',
    href: '/employers'
  }, {
    name: 'Insights',
    href: '/blog'
  }];
  return <>
      {/* Top utility bar */}
      <div className={`fixed top-0 w-full z-50 bg-navy-950 text-white text-xs transition-all duration-300 ${scrolled ? 'h-0 overflow-hidden opacity-0' : 'h-9 opacity-100'}`} data-id="element-105">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 h-full flex items-center justify-between" data-id="element-106">
          <div className="hidden md:flex items-center gap-6 text-gray-300" data-id="element-107">
            <span className="tracking-widest uppercase text-[10px] text-gold-500" data-id="element-108">
              Global Executive Search & Leadership Advisory
            </span>
          </div>
          <div className="flex items-center gap-6" data-id="element-109">
            <a href="tel:+16788073391" className="flex items-center gap-2 text-gray-300 hover:text-gold-500 transition-colors" data-id="element-110">
              <Phone className="h-3 w-3" data-id="element-111" />
              <span data-id="element-112">+1 (678) 807-3391</span>
            </a>
            <span className="text-gray-500 hidden sm:inline" data-id="element-113">|</span>
            <span className="text-gray-400 hidden sm:inline" data-id="element-114">
              42 Countries · 6 Regional Offices
            </span>
          </div>
        </div>
      </div>

      {/* Main nav */}
      <nav className={`fixed w-full z-40 transition-all duration-300 ${scrolled ? 'top-0 bg-white shadow-md py-2' : 'top-9 bg-transparent py-4'}`} data-id="element-115">
        <div className="max-w-7xl mx-auto px-6 lg:px-12" data-id="element-116">
          <div className="flex justify-between items-center" data-id="element-117">
            {/* Logo */}
            <Link to="/" className="flex items-center gap-5 mr-12" data-id="element-118">
              {/* Circular Emblem */}
              <div className="relative flex-shrink-0" data-id="element-119">
                <div className={`w-16 h-16 rounded-full flex items-center justify-center transition-all duration-300 ${scrolled ? 'bg-navy-900 shadow-lg shadow-navy-900/30' : 'bg-white/15 backdrop-blur-md shadow-lg shadow-black/20'}`} data-id="element-120">
                  <div className={`absolute w-14 h-14 rounded-full border transition-all duration-300 ${scrolled ? 'border-gold-500/60' : 'border-white/40'}`} data-id="element-121" />
                  <div className={`absolute w-11 h-11 rounded-full border transition-all duration-300 ${scrolled ? 'border-gold-500/30' : 'border-white/20'}`} data-id="element-122" />
                  <div className="relative z-10 flex flex-col items-center justify-center" data-id="element-123">
                    <span className={`text-base font-serif font-bold leading-none tracking-tight transition-colors duration-300 ${scrolled ? 'text-gold-500' : 'text-white'}`} data-id="element-124">
                      TB
                    </span>
                    <div className={`w-5 h-px mt-0.5 transition-colors duration-300 ${scrolled ? 'bg-gold-500/60' : 'bg-white/50'}`} data-id="element-125" />
                  </div>
                </div>
              </div>

              <div className="flex flex-col leading-none" data-id="element-126">
                <span className={`text-xl font-serif font-bold tracking-wide transition-colors duration-300 ${scrolled ? 'text-navy-900' : 'text-white'}`} data-id="element-127">
                  TalentBridge
                </span>
                <span className={`text-xs font-medium tracking-[0.2em] uppercase mt-0.5 transition-colors duration-300 ${scrolled ? 'text-gold-500' : 'text-white/70'}`} data-id="element-128">
                  Global
                </span>
              </div>
            </Link>

            {/* Desktop Nav */}
            <div className="hidden lg:flex items-center gap-10" data-id="element-129">
              {navLinks.map(link => <Link key={link.name} to={link.href} className={`text-sm font-medium tracking-wide transition-colors hover:text-gold-500 ${scrolled ? 'text-gray-700' : 'text-white/90'}`} data-id="element-130">
                  {link.name}
                </Link>)}
              <Button variant="gold" size="sm" href="mailto:contact@talentbridge.blog" data-id="element-131">
                Book Consultation
              </Button>
            </div>

            {/* Mobile Menu Button */}
            <div className="lg:hidden" data-id="element-132">
              <button onClick={() => setIsOpen(!isOpen)} className={`p-2 rounded-md ${scrolled ? 'text-navy-900' : 'text-white'}`} aria-label="Toggle menu" data-id="element-133">
                {isOpen ? <X className="h-6 w-6" data-id="element-134" /> : <Menu className="h-6 w-6" data-id="element-135" />}
              </button>
            </div>
          </div>
        </div>

        {/* Mobile Menu */}
        {isOpen && <div className="lg:hidden bg-white border-t absolute w-full shadow-lg" data-id="element-136">
            <div className="px-6 pt-2 pb-6 space-y-1" data-id="element-137">
              {navLinks.map(link => <Link key={link.name} to={link.href} className="block px-3 py-3 text-base font-medium text-gray-700 hover:text-navy-900 hover:bg-gray-50 rounded-md border-b border-gray-100" data-id="element-138">
                  {link.name}
                </Link>)}
              <div className="pt-4" data-id="element-139">
                <Button variant="gold" fullWidth href="mailto:contact@talentbridge.blog" data-id="element-140">
                  Book Consultation
                </Button>
              </div>
            </div>
          </div>}
      </nav>
    </>;
}